import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from "@mui/icons-material";
import {
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trailer,
  useArchiveTrailerMutation,
  useDeleteTrailerMutation,
  useRestoreTrailerMutation,
} from "../../../api/trailersApi";
import {
  Column,
  DataTable,
  makeColumnsSearchable,
} from "../../../components/common/DataTable/DataTable";
import { ConfirmationDialog } from "../../../components/common/ConfirmationDialog/ConfirmationDialog";
import { getActiveStatusColor } from "../../../utils/statusColors";
import AuditHistoryDialog from "../../audit/components/AuditHistoryDialog";

interface TrailersTableProps {
  trailers: Trailer[];
  isLoading: boolean;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isLoadingRows?: boolean;
  onLoadMore?: () => void;

  // Search functionality
  enableSearch?: boolean;
  onSearchChange?: (filters: Record<string, any>) => void;
}

const TrailersTable: React.FC<TrailersTableProps> = ({
  trailers,
  isLoading,
  totalCount,
  hasNextPage,
  isLoadingMore,
  isLoadingRows,
  onLoadMore,

  // Search functionality
  enableSearch = false,
  onSearchChange,
}) => {
  const navigate = useNavigate();
  const [deleteTrailer] = useDeleteTrailerMutation();
  const [archiveTrailer, { isLoading: isArchiving }] = useArchiveTrailerMutation();
  const [restoreTrailer] = useRestoreTrailerMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [trailerToDelete, setTrailerToDelete] = useState<Trailer | null>(null);
  const [trailerToArchive, setTrailerToArchive] = useState<Trailer | null>(null);
  const [historyTrailer, setHistoryTrailer] = useState<Trailer | null>(null);

  const handleEditClick = (trailerId: number) => {
    navigate(`/trailers/${trailerId}/edit`);
  };

  const handleDeleteClick = (trailer: Trailer) => {
    setTrailerToDelete(trailer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (trailerToDelete) {
      try {
        await deleteTrailer(trailerToDelete.id).unwrap();
        setDeleteDialogOpen(false);
        setTrailerToDelete(null);
      } catch (error) {
        console.error("Failed to delete trailer:", error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTrailerToDelete(null);
  };

  const handleArchiveConfirm = async () => {
    if (trailerToArchive) {
      await archiveTrailer(trailerToArchive.id);
      setTrailerToArchive(null);
    }
  };

  const handleRestoreClick = async (trailer: Trailer) => {
    await restoreTrailer(trailer.id);
  };

  const columns: Column[] = [
    {
      field: "unitNumber",
      headerName: "Unit Number",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "make",
      headerName: "Make",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "model",
      headerName: "Model",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "year",
      headerName: "Year",
      flex: 1,
      renderCell: (value: number | null) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          {value !== null ? value : "-"}
        </Typography>
      ),
    },
    {
      field: "vin",
      headerName: "VIN",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "serial",
      headerName: "Serial",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "isActive",
      headerName: "Active",
      flex: 1,
      renderCell: (value: boolean) => (
        <span
          style={{
            color: getActiveStatusColor(value),
            fontWeight: "bold",
          }}
        >
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      field: "isArchived",
      headerName: "Archive",
      width: 120,
      renderCell: (value: boolean) =>
        value ? (
          <Chip label="Archived" size="small" color="default" variant="outlined" />
        ) : (
          <Chip label="Live" size="small" color="success" variant="outlined" />
        ),
    },
  ];

  // Define actions for each trailer
  const getActions = (trailer: Trailer) => [
    {
      icon: <EditIcon fontSize="small" />,
      label: "Edit",
      onClick: () => handleEditClick(trailer.id),
      color: "primary" as const,
    },
    {
      icon: <HistoryIcon fontSize="small" />,
      label: "View History",
      onClick: () => setHistoryTrailer(trailer),
      color: "info" as const,
    },
    trailer.isArchived
      ? {
          icon: <UnarchiveIcon fontSize="small" />,
          label: "Restore",
          onClick: () => handleRestoreClick(trailer),
          color: "success" as const,
        }
      : {
          icon: <ArchiveIcon fontSize="small" />,
          label: "Archive",
          onClick: () => setTrailerToArchive(trailer),
          color: "warning" as const,
        },
    {
      icon: <DeleteIcon fontSize="small" />,
      label: "Delete",
      onClick: () => handleDeleteClick(trailer),
      color: "error" as const,
    },
  ];

  // Define searchable columns for trailers
  const searchableColumns = makeColumnsSearchable(
    columns,
    ["unitNumber", "make", "model", "year", "vin", "serial", "isActive"],
    {
      unitNumber: "string",
      make: "string",
      model: "string",
      year: "number",
      vin: "string",
      serial: "string",
      isActive: "string",
    }
  );

  return (
    <>
      <DataTable<Trailer>
        items={trailers}
        columns={columns}
        actions={getActions}
        isLoading={isLoading}
        title="Trailers"
        totalCount={totalCount}
        hasNextPage={hasNextPage}
        isLoadingMore={isLoadingMore}
        isLoadingRows={isLoadingRows}
        onLoadMore={onLoadMore}
        enableSearch={enableSearch}
        searchableColumns={searchableColumns}
        onSearchChange={onSearchChange}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete trailer "
            {trailerToDelete?.unitNumber || trailerToDelete?.id}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(trailerToArchive)}
        title="Archive Trailer"
        message={`Are you sure you want to archive "${
          trailerToArchive?.unitNumber || `Trailer #${trailerToArchive?.id}`
        }"? It will no longer appear on the map, in the sidebar, or in selection lists.`}
        confirmLabel="Archive"
        cancelLabel="Cancel"
        confirmColor="warning"
        onClose={() => setTrailerToArchive(null)}
        onConfirm={handleArchiveConfirm}
        isLoading={isArchiving}
      />

      {historyTrailer && (
        <AuditHistoryDialog
          open={Boolean(historyTrailer)}
          entityType="Trailer"
          entityId={historyTrailer.id}
          title={`History for ${
            historyTrailer.unitNumber || `Trailer #${historyTrailer.id}`
          }`}
          onClose={() => setHistoryTrailer(null)}
        />
      )}
    </>
  );
};

export default TrailersTable;
